import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Mail, MessageSquare, Pencil, Plus, Send, X } from 'lucide-react';
import { useT } from '../i18n';
import { api } from '../api/client';

interface PendingComment { id: number; by: string; byId: number | null; text: string; canEdit: boolean }
interface PendingEntry {
  key: string;
  projectId: number | null;
  projectName: string | null;
  label: string;
  title: string;
  canEdit: boolean;
  note?: { id: number; title: string; text: string };
  comments: PendingComment[];
}
interface PendingRecipient { email: string; firstName: string | null; personName: string; items: number; sendsAt: string; entries: PendingEntry[] }

const POLL_MS = 30_000;

/**
 * Topbar button shown while e-mails generated by MY changes are waiting for
 * their quiet period (server: services/emailBundle.ts). Lists who will be
 * e-mailed and when; each e-mail can be opened to take an item out, comment
 * under an item or add a message, and they can all be sent right away.
 */
export function PendingEmailsButton() {
  const { t } = useT();
  const [pending, setPending] = useState<PendingRecipient[]>([]);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);   // recipient e-mail shown in full
  const [sending, setSending] = useState(false);
  const [sentCount, setSentCount] = useState<number | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const load = useCallback(() => {
    api.getPendingEmails().then((rows) => setPending(rows as PendingRecipient[])).catch(() => {});
  }, []);

  // Poll, and refresh right after the user saves something (any write request).
  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    window.addEventListener('pmo:data-changed', onFocus);
    return () => { clearInterval(id); window.removeEventListener('focus', onFocus); window.removeEventListener('pmo:data-changed', onFocus); };
  }, [load]);

  useEffect(() => {
    if (!open) return;
    load();
    const onDocClick = (e: MouseEvent) => { if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDocClick); document.removeEventListener('keydown', onKey); };
  }, [open, load]);

  // Every edit returns the updated list.
  const edit = async (call: () => Promise<unknown>): Promise<boolean> => {
    try {
      setPending(await call() as PendingRecipient[]);
      return true;
    } catch (err: any) {
      alert(err.message);
      load();
      return false;
    }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const { sent } = await api.sendPendingEmails() as { sent: number };
      setSentCount(sent);
      setPending([]);
      setTimeout(() => { setOpen(false); setSentCount(null); load(); }, 2000);
    } catch (err: any) {
      alert(err.message);
    } finally {
      setSending(false);
    }
  };

  if (pending.length === 0 && sentCount === null) return null;

  const minutesLeft = (iso: string) => Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 60_000));

  return (
    <div className="notification-bell" ref={rootRef}>
      <button type="button" className="notification-bell-btn" onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog" aria-expanded={open} title={t('pendingEmails.title', { n: pending.length })}>
        <Mail size={16} />
        {pending.length > 0 && <span className="notification-badge">{pending.length}</span>}
      </button>
      {open && (
        <div className={`profile-menu-panel pending-emails-panel${expanded ? ' pending-emails-panel--wide' : ''}`} role="dialog" aria-label={t('pendingEmails.heading')}>
          {sentCount !== null ? (
            <div className="pending-emails-done">{t('pendingEmails.sent', { n: sentCount })}</div>
          ) : (
            <>
              <div className="profile-menu-section-label">{t('pendingEmails.heading')}</div>
              <p className="pending-emails-help">{t('pendingEmails.help')}</p>
              <ul className="pending-emails-list">
                {pending.map((p) => {
                  const isOpen = expanded === p.email;
                  return (
                    <li key={p.email}>
                      <button type="button" className="pending-emails-person" aria-expanded={isOpen}
                        onClick={() => setExpanded(isOpen ? null : p.email)}>
                        {isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                        <span className="pending-emails-person-text">
                          <span className="pending-emails-name">{p.personName}</span>
                          <span className="pending-emails-meta">
                            {t('pendingEmails.changes', { n: p.items })} · {t('pendingEmails.inMinutes', { n: minutesLeft(p.sendsAt) })}
                          </span>
                        </span>
                      </button>
                      {isOpen && <PendingEmailDetail recipient={p} edit={edit} />}
                    </li>
                  );
                })}
              </ul>
              <button type="button" className="btn btn-accent btn-sm pending-emails-send" disabled={sending} onClick={sendNow}>
                <Send size={13} /> {t('pendingEmails.sendNow')}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

type Edit = (call: () => Promise<unknown>) => Promise<boolean>; // false: it failed (already reported)

/** One waiting e-mail, item by item, with its editing controls. */
function PendingEmailDetail({ recipient, edit }: { recipient: PendingRecipient; edit: Edit }) {
  const { t } = useT();
  const [adding, setAdding] = useState(false);
  const projects = [...new Map(recipient.entries.filter(e => e.projectId != null).map(e => [e.projectId!, e.projectName ?? ''])).entries()];

  return (
    <div className="pending-emails-detail">
      {recipient.entries.map((e) => (
        <PendingEmailItem key={`${e.projectId ?? 0}:${e.key}`} email={recipient.email} entry={e} edit={edit}
          showProject={projects.length > 1 || e.projectId == null} />
      ))}
      {adding ? (
        <NoteForm projects={projects} onCancel={() => setAdding(false)}
          onSave={async (note) => { if (await edit(() => api.addPendingEmailNote(recipient.email, note))) setAdding(false); }} />
      ) : (
        <button type="button" className="btn btn-ghost btn-sm pending-emails-add" onClick={() => setAdding(true)}>
          <Plus size={13} /> {t('pendingEmails.addNote')}
        </button>
      )}
    </div>
  );
}

function PendingEmailItem({ email, entry, edit, showProject }: { email: string; entry: PendingEntry; edit: Edit; showProject: boolean }) {
  const { t } = useT();
  const myComment = entry.comments.find(c => c.canEdit && c.byId != null);
  const [commenting, setCommenting] = useState(false);
  const [editingNote, setEditingNote] = useState(false);

  const remove = () => {
    if (!window.confirm(t('pendingEmails.confirmRemove', { title: entry.title }))) return;
    edit(() => api.dismissPendingEmailItem(email, entry.projectId, entry.key));
  };

  return (
    <div className="pending-emails-item">
      <div className="pending-emails-item-head">
        <div className="pending-emails-item-text">
          <span className="pending-emails-item-label">
            {entry.label}{showProject && ` · ${entry.projectName ?? t('pendingEmails.noProject')}`}
          </span>
          <span className="pending-emails-item-title">{entry.title}</span>
        </div>
        {entry.canEdit && (
          <div className="pending-emails-item-actions">
            {entry.note ? (
              <button type="button" className="btn-icon" title={t('pendingEmails.editNote')} onClick={() => setEditingNote(true)}><Pencil size={13} /></button>
            ) : (
              <button type="button" className="btn-icon" title={t('pendingEmails.comment')} onClick={() => setCommenting(true)}><MessageSquare size={13} /></button>
            )}
            <button type="button" className="btn-icon" title={t('pendingEmails.remove')} onClick={remove}><X size={13} /></button>
          </div>
        )}
      </div>
      {entry.note?.text && !editingNote && <p className="pending-emails-note-text">{entry.note.text}</p>}
      {editingNote && entry.note && (
        <NoteForm initial={entry.note} onCancel={() => setEditingNote(false)}
          onSave={async (note) => { if (await edit(() => api.updatePendingEmailNote(email, entry.note!.id, note))) setEditingNote(false); }} />
      )}
      {entry.comments.filter(c => !(commenting && c === myComment)).map((c) => (
        <p key={c.id} className="pending-emails-comment">
          <strong>{c.by} :</strong> {c.text}
          {c === myComment && (
            <button type="button" className="btn-icon" title={t('pendingEmails.editComment')} onClick={() => setCommenting(true)}><Pencil size={11} /></button>
          )}
        </p>
      ))}
      {commenting && (
        <CommentForm initial={myComment?.text ?? ''} onCancel={() => setCommenting(false)}
          onSave={async (text) => { if (await edit(() => api.setPendingEmailComment(email, entry.projectId, entry.key, text))) setCommenting(false); }} />
      )}
    </div>
  );
}

function CommentForm({ initial, onSave, onCancel }: { initial: string; onSave: (text: string) => Promise<void>; onCancel: () => void }) {
  const { t } = useT();
  const [text, setText] = useState(initial);
  const [saving, setSaving] = useState(false);
  return (
    <div className="pending-emails-form">
      <textarea className="form-textarea" rows={2} autoFocus value={text} placeholder={t('pendingEmails.commentPlaceholder')}
        onChange={(e) => setText(e.target.value)} />
      <div className="pending-emails-form-actions">
        <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel}>{t('common.cancel')}</button>
        <button type="button" className="btn btn-accent btn-sm" disabled={saving || (!text.trim() && !initial)}
          onClick={async () => { setSaving(true); try { await onSave(text); } finally { setSaving(false); } }}>
          {text.trim() || !initial ? t('common.save') : t('pendingEmails.removeComment')}
        </button>
      </div>
    </div>
  );
}

function NoteForm({ initial, projects, onSave, onCancel }: {
  initial?: { title: string; text: string };
  projects?: [number, string][];   // given when adding: the message can go under one of the e-mail's projects
  onSave: (note: { title: string; text: string; projectId: number | null }) => Promise<void>;
  onCancel: () => void;
}) {
  const { t } = useT();
  const [title, setTitle] = useState(initial?.title ?? '');
  const [text, setText] = useState(initial?.text ?? '');
  const [projectId, setProjectId] = useState<number | null>(projects?.length === 1 ? projects[0][0] : null);
  const [saving, setSaving] = useState(false);
  return (
    <div className="pending-emails-form">
      <input className="form-input" autoFocus value={title} placeholder={t('pendingEmails.noteTitle')} onChange={(e) => setTitle(e.target.value)} />
      <textarea className="form-textarea" rows={3} value={text} placeholder={t('pendingEmails.noteText')} onChange={(e) => setText(e.target.value)} />
      {projects && projects.length > 0 && (
        <select className="form-select" value={projectId ?? ''} onChange={(e) => setProjectId(e.target.value ? Number(e.target.value) : null)}>
          <option value="">{t('pendingEmails.noProject')}</option>
          {projects.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </select>
      )}
      <div className="pending-emails-form-actions">
        <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel}>{t('common.cancel')}</button>
        <button type="button" className="btn btn-accent btn-sm" disabled={saving || (!title.trim() && !text.trim())}
          onClick={async () => { setSaving(true); try { await onSave({ title, text, projectId }); } finally { setSaving(false); } }}>
          {t('common.save')}
        </button>
      </div>
    </div>
  );
}
